/**
 * Visible network/sync state indicator shown in the Header (mobile + desktop).
 * Three states: En ligne (green) / Synchronisation (amber) / Hors ligne (red).
 * Also displays a pending count pill when there are queued mutations.
 */
import { Wifi, WifiOff, RefreshCw } from 'lucide-react'
import { useOfflineSync } from '@/hooks/useOfflineSync'

interface Props {
  onOpenSync?: () => void
}

export default function OfflineIndicator({ onOpenSync }: Props) {
  const { state, pendingCount, deadCount, retryNow } = useOfflineSync()

  const badgeClass =
    state === 'online'
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      : state === 'syncing'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'

  const label =
    state === 'online' ? 'En ligne' : state === 'syncing' ? 'Synchronisation…' : 'Hors ligne'

  const Icon = state === 'syncing' ? RefreshCw : state === 'online' ? Wifi : WifiOff

  const handleClick = () => {
    if (state !== 'online') {
      retryNow()
    }
    if (pendingCount > 0 || deadCount > 0) {
      onOpenSync?.()
    }
  }

  // Hide completely when everything is green and nothing pending
  if (state === 'online' && pendingCount === 0 && deadCount === 0) return null

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${badgeClass}`}
      title={state !== 'online' ? 'Appuyer pour réessayer' : 'Voir les modifications en attente'}
      aria-label={`${label}${pendingCount > 0 ? `, ${pendingCount} en attente` : ''}`}
    >
      <Icon size={12} className={state === 'syncing' ? 'animate-spin' : ''} />
      <span className="hidden sm:inline">{label}</span>
      {(pendingCount > 0 || deadCount > 0) && (
        <span className="ml-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-white/70 dark:bg-black/30 px-1.5 text-[10px] font-bold">
          {pendingCount + deadCount}
        </span>
      )}
    </button>
  )
}
